import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const PublicHeader = ({
  currentUser = null,
  showAuthButtons = true,
  showNavigation = true,
  className = ""
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Use user from auth context if currentUser prop is not provided
  const currentUserData = currentUser || user;

  const handleLogout = () => {
    logout();
    navigate('/public/login');
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Navigation items for public pages
  const publicNavItems = [
    { label: 'Jobs', path: '/public/jobs', icon: 'Search' },
    { label: 'About', path: '/public/about', icon: 'Info' },
    { label: 'Contact', path: '/public/contact', icon: 'MessageCircle' }
  ];

  // Candidate navigation items (when logged in as candidate)
  const candidateNavItems = [
    { label: 'Dashboard', path: '/public/candidate-dashboard', icon: 'LayoutDashboard' },
    { label: 'Browse Jobs', path: '/public/jobs', icon: 'Search' },
    { label: 'My Applications', path: '/candidate/applications', icon: 'FileText' }
  ];

  const navItems = currentUserData?.user_type === 'candidate' ? candidateNavItems : publicNavItems;

  return (
    <header className={`bg-white shadow-sm border-b sticky top-0 z-[100] ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon name="Brain" size={20} color="white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-900">C-success recruit</h1>
                <p className="text-xs text-gray-600 -mt-1">
                  {currentUserData?.user_type === 'candidate' ? 'Candidate Portal' : 'Intelligent Recruitment'}
                </p>
              </div>
              {/* Compact logo for mobile */}
              <div className="block sm:hidden">
                <h1 className="text-base font-semibold text-gray-900">CSR</h1>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          {showNavigation && (
            <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`
                    flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isActivePath(item.path)
                      ? 'text-blue-700 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon name={item.icon} size={16} />
                  <span className="hidden lg:inline">{item.label}</span>
                </button>
              ))}
            </nav>
          )}

          {/* Right Section */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {currentUserData ? (
              // Logged in user menu
              <div className="flex items-center space-x-2 sm:space-x-4">
                <span className="text-sm text-gray-600 hidden sm:block">
                  Welcome, {currentUserData.name}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  iconName="LogOut"
                  className="hidden sm:inline-flex"
                >
                  Sign Out
                </Button>
                {/* Compact sign out for mobile */}
                <button
                  onClick={handleLogout}
                  className="sm:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Icon name="LogOut" size={20} />
                </button>
              </div>
            ) : showAuthButtons ? (
              // Authentication buttons for non-logged in users
              <div className="hidden md:flex items-center space-x-4">
                <button
                  onClick={() => navigate('/public/login')}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Sign In
                </button>
                <Button
                  onClick={() => navigate('/public/register')}
                  size="sm"
                >
                  Register
                </Button>
              </div>
            ) : null}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && showNavigation && (
          <div className="md:hidden border-t border-gray-200">
            <nav className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors
                    ${isActivePath(item.path)
                      ? 'text-blue-700 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.label}</span>
                </button>
              ))}

              {/* Mobile Auth Buttons */}
              {!currentUserData && showAuthButtons && (
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => {
                      navigate('/public/login');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      navigate('/public/register');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Register
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicHeader;